import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
    Select, InputLabel, MenuItem, FormControl, Divider, TextField, Grid, IconButton,
    InputAdornment, Breadcrumbs, Typography
} from "@material-ui/core";
import { Search, Refresh, ClearAll, Home } from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { Route, Link, useNavigate, Routes, useLocation } from "react-router-dom"
import LoadDetails from './LoadDetails';
import { DataGrid, GridColDef, GridRowsProp, GridRowData, GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

let sTimer: NodeJS.Timeout;

require('events').EventEmitter.prototype._maxListeners = 20;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 650,
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
        },
        header: {
            display: "flex"
        },
        margin: {
            margin: theme.spacing(1),
        },
        input: {
            width: 300
        },
        progress: {
            backgroundColor: "#ff9800"
        },
        progressBackground: {
            backgroundColor: "#ffe0b2"
        },
        icon: {
            marginRight: theme.spacing(0.5),
            width: 20,
            height: 20,
        },
        link: {
            display: "flex"
        },
        dataGrid: {
            backgroundColor: "white"
        }
    })
);
export interface LoadInfo {
    loadNo: number
    vehicleNo: string
    loadStartedAt: Time
    loadStartedBy: string
    loadCompletedBy: string
    loadCompletedAt: Time
    dispatchedAt: Time
    dispatchedBy: string
    loadType: string
    loadStatus: string
    totalLoadedWeight: number
    totalLoadedTags: number
    numJobs: number
    location: String
    freightCo: String
}

export interface Time {
    Time: string;
    Valid: boolean;
}

interface Props {
    user?: any | null;
    globalBranch: string
}

export default function EncoreLoads({ user, globalBranch }: Props) {
    const classes = useStyles();

    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [loads, setLoads] = useState<Array<LoadInfo>>([]);
    const [type, setType] = useState('active');
    const [searchInput, setSearchInput] = useState<string>('');

    // let { path } = useRouteMatch()
    const location = useLocation()

    useEffect(() => {
        let isMounted = true;

        setIsLoaded(false)
        setLoads([])
        fetch(`/api/loads/${type}/type/tags`)
            .then(res => res.json())
            .then((result: Array<LoadInfo>) => {
                if (isMounted) {
                    if (result) {
                        setLoads(result)
                    } else {
                        setLoads([])
                    }
                    setIsLoaded(true)
                }
            })
            .catch((error: any) => {
                console.log("Error fetching loads", error)
                setIsLoaded(true)
            })

        return () => {
            isMounted = false;
        }
    }, [])

    const handleType = (event: React.ChangeEvent<{ value: unknown }>) => {
        onLoadTypeChanged(event.target.value as string);
    };

    const handleSearch = (event: React.ChangeEvent<{ value: string }>) => {
        onSearchInput(event.target.value)
    }

    const handleClearSearch = () => {
        if (searchInput.length !== 0) {
            setSearchInput('')
            getLoads(type)
        }
    }

    function getLoads(type: string) {
        setIsLoaded(false)
        setLoads([])
        fetch(`/api/loads/${type}/type/tags`)
            .then(res => res.json())
            .then((result: Array<LoadInfo>) => {
                if (result) {
                    setLoads(result)
                } else {
                    setLoads([])
                }
                setIsLoaded(true)
            })
    }

    const handleRefresh = () => {
        getLoads(type)
    }

    function onLoadTypeChanged(type: string) {
        setType(type);
        if (searchInput.length === 0) {
            getLoads(type)
        } else {
            setIsLoaded(false)
            setLoads([])
            fetch(`/api/loads/status/${type}/type/tags/search/${searchInput}`)
                .then(res => res.json())
                .then((result: Array<LoadInfo>) => {
                    if (result) {
                        setLoads(result)
                    } else {
                        setLoads([])
                    }
                    setIsLoaded(true)
                })
        }
    }

    function onSearchInput(input: string) {
        setSearchInput(input)
        if (sTimer !== null) {
            clearTimeout(sTimer);
        }
        sTimer = setTimeout(() => {
            if (input.length > 0) {
                setIsLoaded(false)
                setLoads([])
                fetch(`/api/loads/status/${type}/type/tags/search/${input}`)
                    .then(res => res.json())
                    .then((result: Array<LoadInfo>) => {
                        if (result) {
                            setLoads(result)
                        } else {
                            setLoads([])
                        }
                        setIsLoaded(true)
                    })
            }
        }, 1500);
    }

    return (
        <div>
            <Breadcrumbs>
                <Link to="/encore" className={classes.link}>
                    <Home className={classes.icon} />
                    Home
                </Link>
                <Typography color="textPrimary">Loads</Typography>
            </Breadcrumbs>
            <div className={classes.header}>
                <h2 style={{ flexGrow: 1 }}>
                    Loads
                </h2>
                <div className={classes.margin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <Search />
                        </Grid>
                        <Grid item>
                            <TextField
                                id="input-with-icon-grid"
                                label="Search by Vehicle or Job No "
                                onChange={handleSearch}
                                value={searchInput}
                                InputProps={{
                                    className: classes.input,
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton color="inherit" onClick={handleClearSearch}>
                                            <ClearAll />
                                        </IconButton>
                                    </InputAdornment>
                                }} />
                        </Grid>
                    </Grid>
                </div>
                <FormControl className={classes.formControl}>
                    <InputLabel id="demo-simple-select-label">Type</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={type}
                        onChange={handleType}>
                        <MenuItem value={'active'}>Active</MenuItem>
                        <MenuItem value={'dispatched'}>Dispatched</MenuItem>
                    </Select>
                </FormControl>
                <IconButton color="inherit" onClick={handleRefresh}>
                    <Refresh />
                </IconButton>
            </div>
            <Divider style={{ marginBottom: 16 }} />
            <div style={{ height: 800, width: '100%' }}>
                {type === "active" ?
                    <ActiveLoadsTable loading={!isLoaded} globalBranch={globalBranch} loads={loads} searchInput={searchInput} />
                    :
                    <DispatchedLoadsTable loading={!isLoaded} globalBranch={globalBranch} loads={loads} searchInput={searchInput} />
                }
            </div>
        </div>

    )
}

interface LoadsTableProps {
    loading?: boolean
    globalBranch: string
    loads: Array<LoadInfo>
    searchInput: string
}

export function DispatchedLoadsTable({ loading, globalBranch, loads, searchInput }: LoadsTableProps) {

    const classes = useStyles()

    const navigate = useNavigate()

    const gridWrapperRef = useRef<HTMLDivElement>(null);

    // useLayoutEffect(() => {
    //     const gridDiv = gridWrapperRef.current;
    //     if (gridDiv){
    //         const gridEl: HTMLDivElement = gridDiv.querySelector('div')!;
    //         gridEl.style.height = '';
    //     }
    // });

    const dispatchedLoadsColumns: GridColDef[] = [
        { field: 'loadNo', hide: true },
        { field: 'vehicleNo', headerName: 'Vehicle No', width: 125 },
        { field: 'loadStartedBy', headerName: 'Started By', width: 165 },
        {
            field: 'loadStartedAt', headerName: 'Started At', width: 170, type: 'dateTime',
            valueFormatter: ((params: GridValueFormatterParams) => formatDateString(params.value as Date))
        },
        { field: 'loadCompletedBy', headerName: 'Completed By', width: 155 },
        {
            field: 'loadCompletedAt', headerName: 'Completed At', width: 170, type: 'dateTime',
            valueFormatter: ((params: GridValueFormatterParams) => formatDateString(params.value as Date))
        },
        { field: 'dispatchedBy', headerName: 'Dispatched By', width: 155 },
        {
            field: 'dispatchedAt', headerName: 'Dispatched At', width: 170, type: 'dateTime',
            valueFormatter: ((params: GridValueFormatterParams) => formatDateString(params.value as Date))
        },
        { field: 'totalLoadedWeight', headerName: 'Total Weight', width: 150 },
        { field: 'numJobs', headerName: '# Jobs', width: 100 },
        { field: 'location', headerName: 'Location', width: 150 },
        { field: 'freightCo', headerName: 'Freight Co', width: 350 },
    ]

    function getTableRows(): GridRowsProp {
        let rows: GridRowData[] = []
        // Filter base on global branch 
        
        // let filteredList = loads?.filter((load: LoadInfo) => globalBranch.toLowerCase().includes(load.location.toLowerCase(),0))
        let filteredList = loads?.filter((load: LoadInfo) => globalBranch === "" ? true :  globalBranch.toLowerCase().includes(load.location.toLowerCase(),0))
        if (filteredList) {
            console.log("filteredList",filteredList)
            filteredList.forEach((load: LoadInfo) => {

                let obj: any = {
                    id: load.loadNo,
                    ...load,
                    loadStartedAt: formatDate(load.loadStartedAt),
                    loadCompletedAt: formatDate(load.loadCompletedAt),
                    dispatchedAt: formatDate(load.dispatchedAt)
                }
                rows.push(obj)
            })
        }
        return rows
    }

    function formatDate(date: Time): Date | string {
        return date.Valid ? parseISO(date.Time) : "";
    }

    const formatDateString = (date: Date | string) => {
        if (typeof (date) === 'string') {
            return ""
        }

        return format(date, "dd/MM/yyyy HH:mm")
    }

    return (
        <div ref={gridWrapperRef}>
            <DataGrid
                className={classes.dataGrid}
                autoHeight
                loading={loading}
                rows={getTableRows()}
                columns={dispatchedLoadsColumns}
                pageSize={25}
                onRowClick={(params: GridRowParams) => {
                    navigate(`/encore/loads/${params.id}`, { state: { type: 'dispatched', selectedLoadIdx: params.row.rowIndex, loads: loads, searchInput: searchInput } })
                }} />
        </div>

    )
}

export function ActiveLoadsTable({ loading, globalBranch, loads, searchInput }: LoadsTableProps) {

    const classes = useStyles()

    const navigate = useNavigate()

    const gridWrapperRef = useRef<HTMLDivElement>(null);

    // useLayoutEffect(() => {
    //     const gridDiv = gridWrapperRef.current;
    //     if (gridDiv){
    //         const gridEl: HTMLDivElement = gridDiv.querySelector('div')!;
    //         gridEl.style.height = '';
    //     }
    // });

    const activeLoadsColumns: GridColDef[] = [
        { field: 'loadNo', hide: true },
        { field: 'vehicleNo', headerName: 'Vehicle No', width: 125 },
        { field: 'loadStartedBy', headerName: 'Load Started By', width: 165 },
        {
            field: 'loadStartedAt', headerName: 'Load Started At', width: 170, type: 'dateTime',
            valueFormatter: ((params: GridValueFormatterParams) => formatDateString(params.value as Date))
        },
        { field: 'totalLoadedWeight', headerName: 'Total Weight', width: 155 },
        { field: 'numJobs', headerName: '# Jobs', width: 100 },
        { field: 'location', headerName: 'Location', width: 150 },
        { field: 'freightCo', headerName: 'Freight Co', width: 350 },
    ]

    function getTableRows(): GridRowsProp {
        let rows: GridRowData[] = []
        let filteredList = loads?.filter((load: LoadInfo) => globalBranch === "" ? true :  globalBranch.toLowerCase().includes(load.location.toLowerCase(),0))
        if (filteredList) {
            filteredList.forEach((load: LoadInfo) => {

                let obj: any = {
                    id: load.loadNo,
                    ...load,
                    loadStartedAt: formatDate(load.loadStartedAt),
                    loadCompletedAt: formatDate(load.loadCompletedAt),
                    dispatchedAt: formatDate(load.dispatchedAt)
                }
                rows.push(obj)
            })
        }
        return rows
    }

    const formatDateString = (date: Date | string) => {
        if (typeof (date) === 'string') {
            return ""
        }

        return format(date, "dd/MM/yyyy HH:mm")
    }

    function formatDate(date: Time): Date | string {
        return date.Valid ? parseISO(date.Time) : "";
    }

    return (
        <div ref={gridWrapperRef}>
            <DataGrid
                className={classes.dataGrid}
                autoHeight
                loading={loading}
                rows={getTableRows()}
                columns={activeLoadsColumns}
                pageSize={25}
                onRowClick={(params: GridRowParams) => {
                    navigate(`/encore/loads/${params.id}`, { state: { type: 'active', selectedLoadIdx: params.row.rowIndex, loads: loads, searchInput: searchInput } })
                }} />
        </div>
    )
}